import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { User, Mail, Clock, Bell, LogOut, Save, Loader2, Globe, Moon, Sun, Palette } from 'lucide-react';
import { toast } from 'sonner';
import AppLayout from '../components/AppLayout';
import api from '../lib/api';

const Settings = () => {
  const { user, logout, refreshUser } = useAuth();
  const { language, languages, changeLanguage, t } = useLanguage();
  const { theme, isDark, toggleTheme, setDarkMode } = useTheme();
  const [saving, setSaving] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [settings, setSettings] = useState({
    name: user?.name || '',
    email: user?.email || '',
    timezone: user?.timezone || 'Asia/Kolkata',
    emailNotification: false,
    notifications: true,
    focusMode: false,
    notificationPreferences: {
      task: true,
      task_task_assigned: true,
      task_task_awaiting_review: true,
      task_task_approved: true,
      task_task_status_changed: true,
      chat: true,
      form: true,
      task_overdue: true
    }
  });

  // Fetch user settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/user/settings');
        setSettings(prev => ({
          ...prev,
          timezone: response.data.timezone || 'Asia/Kolkata',
          emailNotification: response.data.email_notification || false,
          notificationPreferences: {
            ...prev.notificationPreferences,
            ...(response.data.notification_preferences || {})
          }
        }));
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/user/settings', {
        timezone: settings.timezone,
        email_notification: settings.emailNotification,
        notification_preferences: settings.notificationPreferences
      });
      toast.success(t('save_changes', 'Settings saved successfully!'));
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    window.location.href = '/';
  };

  const handleLanguageChange = async (newLang) => {
    await changeLanguage(newLang);
    toast.success('Language updated!');
  };

  const timezones = [
    { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'British Time (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' }
  ];

  return (
    <AppLayout>
      <div className="h-full overflow-auto bg-background">
        <div className="p-6 md:p-8 max-w-3xl mx-auto pb-20" data-testid="settings-page">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              {t('settings', 'Settings')}
            </h1>
            <p className="text-muted-foreground">{t('preferences', 'Manage your account and preferences')}</p>
          </div>

          {/* Profile Section */}
          <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700" data-testid="profile-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                <User className="w-5 h-5 text-[#4A9CC7]" />
                {t('profile', 'Profile')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#4A9CC7] flex items-center justify-center text-white text-2xl font-bold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-semibold text-lg dark:text-gray-100">{user?.name}</p>
                  <p className="text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="dark:text-gray-300">{t('name', 'Name')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="name"
                      value={settings.name}
                      onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                      className="pl-10 rounded-xl dark:bg-slate-900 dark:border-slate-600"
                      data-testid="name-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="dark:text-gray-300">{t('email', 'Email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      value={settings.email}
                      disabled
                      className="pl-10 rounded-xl bg-muted dark:bg-slate-900 dark:border-slate-600"
                      data-testid="email-input"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Language Section */}
          <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700" data-testid="language-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                <Globe className="w-5 h-5 text-[#4A9CC7]" />
                {t('language', 'Language')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label className="dark:text-gray-300">{t('language', 'Select Language')}</Label>
                <Select 
                  value={language} 
                  onValueChange={handleLanguageChange}
                >
                  <SelectTrigger className="rounded-xl dark:bg-slate-900 dark:border-slate-600" data-testid="language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    {Object.entries(languages).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Choose your preferred language for the app interface
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Appearance Section - Dark Mode */}
          <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700" data-testid="appearance-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                <Palette className="w-5 h-5 text-[#4A9CC7]" />
                {t('appearance', 'Appearance')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dark Mode Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                    isDark 
                      ? 'bg-slate-700 text-yellow-400' 
                      : 'bg-sky-100 text-sky-600'
                  }`}>
                    {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-medium dark:text-gray-100">{t('dark_mode', 'Dark Mode')}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {isDark ? 'Dark theme is enabled' : 'Light theme is enabled'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={isDark}
                  onCheckedChange={setDarkMode}
                  data-testid="dark-mode-switch"
                  className="data-[state=checked]:bg-[#4A9CC7]"
                />
              </div>

              {/* Theme Preview */}
              <div className="border dark:border-slate-700 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">Theme Preview</p>
                <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[#4A9CC7] flex items-center justify-center text-white text-sm font-bold">
                      C
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Sample Task</p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Today at 3:00 PM</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'
                    }`}>High</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
                    }`}>Medium</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
                    }`}>Low</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preferences Section */}
          <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700" data-testid="preferences-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                <Clock className="w-5 h-5 text-[#4A9CC7]" />
                {t('preferences', 'Preferences')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="timezone" className="dark:text-gray-300">{t('timezone', 'Timezone')}</Label>
                <Select 
                  value={settings.timezone} 
                  onValueChange={(value) => setSettings({ ...settings, timezone: value })}
                >
                  <SelectTrigger className="rounded-xl dark:bg-slate-900 dark:border-slate-600" data-testid="timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    {timezones.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Notifications Section */}
          <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700" data-testid="notifications-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                <Bell className="w-5 h-5 text-[#4A9CC7]" />
                {t('notifications', 'Notifications')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium dark:text-gray-100">{t('push_notifications', 'Push Notifications')}</p>
                  <p className="text-sm text-muted-foreground">Receive task reminders and updates</p>
                </div>
                <Switch
                  checked={settings.notifications}
                  onCheckedChange={(checked) => setSettings({ ...settings, notifications: checked })}
                  data-testid="push-notifications-switch"
                  className="data-[state=checked]:bg-[#4A9CC7]"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium dark:text-gray-100">{t('email_reminders', 'Daily Email Digest')}</p>
                  <p className="text-sm text-muted-foreground">Receive daily summary of tasks, forms & messages (weekdays at 9 AM)</p>
                </div>
                <Switch
                  checked={settings.emailNotification}
                  onCheckedChange={(checked) => setSettings({ ...settings, emailNotification: checked })}
                  data-testid="email-notification-switch"
                  className="data-[state=checked]:bg-[#4A9CC7]"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium dark:text-gray-100">{t('focus_mode_setting', 'Focus Mode')}</p>
                  <p className="text-sm text-muted-foreground">Mute all notifications during focus sessions</p>
                </div>
                <Switch
                  checked={settings.focusMode}
                  onCheckedChange={(checked) => setSettings({ ...settings, focusMode: checked })}
                  data-testid="focus-mode-switch"
                  className="data-[state=checked]:bg-[#4A9CC7]"
                />
              </div>

              {/* Granular Notification Preferences */}
              <div className="pt-4 border-t dark:border-slate-700">
                <p className="font-medium dark:text-gray-100 mb-3">Notification Types</p>
                <p className="text-sm text-muted-foreground mb-4">Choose which notifications you want to receive</p>
                <div className="space-y-3">
                  {[
                    { key: 'task_task_assigned', label: 'Task Assignments', desc: 'When a task is assigned to you' },
                    { key: 'task_task_awaiting_review', label: 'Awaiting Review', desc: 'When a task is moved to Awaiting Review' },
                    { key: 'task_task_approved', label: 'Task Approved', desc: 'When your task is approved by a manager' },
                    { key: 'task_task_status_changed', label: 'Status Changes', desc: 'When a task status is changed by a manager' },
                    { key: 'task_overdue', label: 'Overdue Alerts', desc: 'When your tasks become overdue' },
                    { key: 'chat', label: 'Chat Messages', desc: 'New messages in conversations' },
                    { key: 'form', label: 'Form Submissions', desc: 'New form submissions and approvals' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between py-1">
                      <div>
                        <p className="text-sm font-medium dark:text-gray-200">{item.label}</p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                      <Switch
                        checked={settings.notificationPreferences[item.key] !== false}
                        onCheckedChange={(checked) => setSettings(prev => ({
                          ...prev,
                          notificationPreferences: { ...prev.notificationPreferences, [item.key]: checked }
                        }))}
                        data-testid={`notif-pref-${item.key}`}
                        className="data-[state=checked]:bg-[#4A9CC7]"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-full"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {t('save_changes', 'Save Changes')}
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="flex-1 border-red-200 text-red-600 hover:bg-red-50 rounded-full"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('logout', 'Log Out')}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Settings;
